import { useEffect, useState } from "react";
import { Smartphone, CreditCard, Banknote, Loader2, CheckCircle2, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { money } from "@/lib/format";
import { validPhone } from "@/components/PassengerForm";

const PROVIDERS = [
  { id: "MTN MoMo", color: "bg-yellow-300 text-slate-900", ussd: "*170#" },
  { id: "Telecel Cash", color: "bg-red-500 text-white", ussd: "*110#" },
  { id: "AirtelTigo Money", color: "bg-sky-500 text-white", ussd: "*110#" },
];

const Simulated = () => (
  <p className="flex items-center gap-1.5 rounded-lg bg-amber-50 px-3 py-2 text-[11px] font-semibold text-amber-800" data-testid="payment-simulated-note">
    <ShieldCheck className="h-3.5 w-3.5" /> Demo mode: payment is simulated - no real money is charged.
  </p>
);

export function MomoForm({ total, defaultPhone, onPaid, busy, live, mode }) {
  const [provider, setProvider] = useState(PROVIDERS[0].id);
  const [phone, setPhone] = useState(defaultPhone || "");
  const [count, setCount] = useState(null);
  useEffect(() => {
    if (count === null) return;
    if (count === 0) { onPaid({ provider, number: phone }); return; }
    const t = setTimeout(() => setCount(count - 1), 1000);
    return () => clearTimeout(t);
  }, [count]); // eslint-disable-line react-hooks/exhaustive-deps
  const p = PROVIDERS.find((x) => x.id === provider);

  if (count !== null || busy) {
    return (
      <div className="flex flex-col items-center gap-4 py-6 text-center" data-testid="momo-waiting">
        <div className={`pulse-ring grid h-20 w-20 place-items-center rounded-full ${p.color}`}><Smartphone className="h-9 w-9" /></div>
        <div className="font-display text-xl font-bold">Approve on your phone</div>
        <p className="max-w-xs text-sm text-slate-600">A {provider} prompt for <b>{money(total)}</b> was sent to <b>{phone}</b>. Enter your PIN to approve (or dial {p.ussd}).</p>
        <div className="flex items-center gap-2 text-sm font-semibold text-[#0052FF]"><Loader2 className="h-4 w-4 animate-spin" />{busy ? "Confirming payment..." : `Waiting for approval... ${count}s`}</div>
      </div>
    );
  }
  return (
    <div className="space-y-4" data-testid="momo-form">
      <div className="grid grid-cols-3 gap-2">
        {PROVIDERS.map((x) => (
          <button key={x.id} type="button" onClick={() => setProvider(x.id)} data-testid={`momo-provider-${x.id.split(" ")[0].toLowerCase()}`}
            className={`btn-press rounded-xl border-2 p-3 text-xs font-bold ${provider === x.id ? "border-[#0052FF] bg-blue-50" : "border-slate-200 bg-white hover:border-blue-200"}`}>
            <span className={`mx-auto mb-1.5 grid h-8 w-8 place-items-center rounded-full ${x.color}`}><Smartphone className="h-4 w-4" /></span>{x.id}
          </button>
        ))}
      </div>
      <div className="space-y-1.5">
        <Label className="text-xs font-semibold text-slate-600">Mobile money number</Label>
        <Input value={phone} onChange={(e) => setPhone(e.target.value)} inputMode="tel" placeholder="024 123 4567" data-testid="momo-phone-input" />
      </div>
      {live ? (
        <p className="flex items-center gap-1.5 rounded-lg bg-emerald-50 px-3 py-2 text-[11px] font-semibold text-emerald-800" data-testid="payment-live-note">
          <ShieldCheck className="h-3.5 w-3.5" /> Secured by Hubtel{mode === "teller" ? " - customer approves the prompt on their phone" : " - approve the prompt on your phone"}.
        </p>
      ) : <Simulated />}
      <Button disabled={!validPhone(phone)} onClick={() => (live ? onPaid({ provider, number: phone }) : setCount(4))} className="h-12 w-full rounded-xl bg-[#0052FF] text-base font-bold hover:bg-[#0041CC] btn-press" data-testid="momo-pay-button">
        {`Send payment prompt - ${money(total)}`}
      </Button>
    </div>
  );
}

export function CardForm({ total, onPaid, busy, live, mode }) {
  const [c, setC] = useState({ number: "", exp: "", cvv: "", name: "" });
  if (live && mode !== "teller") {
    return (
      <div className="space-y-4" data-testid="card-form">
        <div className="rounded-2xl bg-gradient-to-br from-[#0052FF] to-[#38BDF8] p-5 text-white">
          <CreditCard className="h-6 w-6" />
          <div className="mt-3 font-display text-lg font-bold">Pay {money(total)} by Visa or Mastercard</div>
          <p className="text-sm text-blue-50">You'll enter your card on Hubtel's secure checkout page, then return here for your ticket.</p>
        </div>
        <Button disabled={busy} onClick={() => onPaid({})} className="h-12 w-full rounded-xl bg-[#0052FF] text-base font-bold hover:bg-[#0041CC] btn-press" data-testid="card-pay-button">
          {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : "Continue to secure checkout"}
        </Button>
      </div>
    );
  }
  return <SimCardForm total={total} onPaid={onPaid} busy={busy} c={c} setC={setC} />;
}

function SimCardForm({ total, onPaid, busy, c, setC }) {
  const digits = c.number.replace(/\D/g, "");
  const ok = digits.length >= 15 && /^\d{2}\/\d{2}$/.test(c.exp) && c.cvv.length >= 3 && c.name.trim().length > 1;
  const fmtNum = (v) => v.replace(/\D/g, "").slice(0, 16).replace(/(\d{4})(?=\d)/g, "$1 ");
  const fmtExp = (v) => v.replace(/\D/g, "").slice(0, 4).replace(/(\d{2})(\d)/, "$1/$2");
  return (
    <div className="space-y-4" data-testid="card-form">
      <div className="rounded-2xl bg-gradient-to-br from-[#0052FF] to-[#38BDF8] p-5 text-white shadow-lg">
        <div className="flex justify-between text-xs font-bold uppercase tracking-widest opacity-90"><span>Debit / Credit</span><CreditCard className="h-5 w-5" /></div>
        <div className="mt-6 font-mono text-lg tracking-widest">{c.number || "•••• •••• •••• ••••"}</div>
        <div className="mt-3 flex justify-between text-xs uppercase"><span>{c.name || "Card holder"}</span><span>{c.exp || "MM/YY"}</span></div>
      </div>
      <Input placeholder="Card number" inputMode="numeric" value={c.number} onChange={(e) => setC({ ...c, number: fmtNum(e.target.value) })} data-testid="card-number-input" />
      <div className="grid grid-cols-2 gap-3">
        <Input placeholder="MM/YY" value={c.exp} onChange={(e) => setC({ ...c, exp: fmtExp(e.target.value) })} data-testid="card-expiry-input" />
        <Input placeholder="CVV" inputMode="numeric" type="password" value={c.cvv} onChange={(e) => setC({ ...c, cvv: e.target.value.replace(/\D/g, "").slice(0, 4) })} data-testid="card-cvv-input" />
      </div>
      <Input placeholder="Name on card" value={c.name} onChange={(e) => setC({ ...c, name: e.target.value })} data-testid="card-name-input" />
      <Simulated />
      <Button disabled={!ok || busy} onClick={() => onPaid({ last4: digits.slice(-4), brand: digits[0] === "4" ? "Visa" : "Mastercard" })} className="h-12 w-full rounded-xl bg-[#0052FF] text-base font-bold hover:bg-[#0041CC] btn-press" data-testid="card-pay-button">
        {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <>Pay {money(total)}</>}
      </Button>
    </div>
  );
}

export function CashForm({ total, mode, onPaid, busy }) {
  const [tendered, setTendered] = useState("");
  const change = Number(tendered || 0) - total;
  if (mode !== "teller") {
    return (
      <div className="space-y-4" data-testid="cash-form">
        <div className="rounded-2xl border border-blue-100 bg-blue-50/70 p-4 text-sm text-slate-700">
          <div className="mb-1 flex items-center gap-2 font-bold text-slate-900"><Banknote className="h-4 w-4 text-[#0052FF]" />Reserve now, pay at the station</div>
          Your seats will be held. Pay <b>{money(total)}</b> at any GPRTU ticket counter with your booking code before departure. Unpaid reservations are released when the bus leaves.
        </div>
        <Button disabled={busy} onClick={() => onPaid({})} className="h-12 w-full rounded-xl bg-[#0052FF] text-base font-bold hover:bg-[#0041CC] btn-press" data-testid="cash-reserve-button">
          {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : "Reserve my seats"}
        </Button>
      </div>
    );
  }
  return (
    <div className="space-y-4" data-testid="cash-form">
      <div className="rounded-2xl bg-slate-50 p-4 text-center"><div className="label-xs">Amount due</div><div className="font-display text-3xl font-extrabold">{money(total)}</div></div>
      <div className="space-y-1.5">
        <Label className="text-xs font-semibold text-slate-600">Cash received from customer</Label>
        <Input value={tendered} onChange={(e) => setTendered(e.target.value.replace(/[^\d.]/g, ""))} inputMode="decimal" placeholder="0.00" className="h-12 text-lg font-bold" data-testid="cash-tendered-input" />
      </div>
      <div className="flex flex-wrap gap-2">
        {[total, 50, 100, 200, 500].filter((v, i, a) => a.indexOf(v) === i && v >= total).map((v) => (
          <button key={v} type="button" onClick={() => setTendered(String(v))} className="chip btn-press border border-blue-200 bg-white px-3 py-1.5 text-[#0052FF] hover:bg-blue-50" data-testid={`cash-quick-${v}`}>{v === total ? "Exact" : money(v)}</button>
        ))}
      </div>
      {tendered && <div className={`rounded-xl p-3 text-sm font-bold ${change >= 0 ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-700"}`} data-testid="cash-change-display">{change >= 0 ? `Change to give: ${money(change)}` : `Short by ${money(-change)}`}</div>}
      <Button disabled={busy || change < 0 || !tendered} onClick={() => onPaid({ tendered: Number(tendered), change })} className="h-12 w-full rounded-xl bg-emerald-600 text-base font-bold hover:bg-emerald-700 btn-press" data-testid="cash-confirm-button">
        {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <><CheckCircle2 className="h-5 w-5" />Confirm cash received</>}
      </Button>
    </div>
  );
}
